import { memo } from 'react';
import { Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';

/**
 * Footer of the studio editor: Save changes / Cancel, plus either the note that role access is
 * removed in Roles, or Remove access for a direct-only studio.
 *
 * @param {object} props
 * @param {boolean} props.canSave There are changes to save.
 * @param {boolean} props.isSaving
 * @param {boolean} props.hasRoleAccess A role grants part of the studio.
 * @param {boolean} props.isDirectOnly The studio is only granted directly.
 * @param {string} props.memberName First name, for the role note.
 * @param {string[]} props.roleNames Roles that grant part of the studio.
 * @param {() => void} props.onSave
 * @param {() => void} props.onCancel
 * @param {() => void} props.onRemoveAccess
 */
const StudioEditFooter = ({
  canSave,
  isSaving,
  hasRoleAccess,
  isDirectOnly,
  memberName,
  roleNames,
  onSave,
  onCancel,
  onRemoveAccess,
}) => {
  const { t } = useTranslation('people-access');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-edge pt-4">
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={!canSave} loading={isSaving}>
          {t('studios.save')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>
          {t('studios.cancel')}
        </Button>
      </div>
      <div className="min-w-0 max-w-sm text-right text-xs text-content-muted">
        {hasRoleAccess ? <p>{t('studios.roleNote', { name: memberName, roles: roleNames.join(', ') })}</p> : null}
        {isDirectOnly ? (
          <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
            <Button variant="danger" size="sm" onClick={onRemoveAccess} disabled={isSaving}>
              {t('studios.removeAccess')}
            </Button>
          </Can>
        ) : null}
      </div>
    </div>
  );
};

export default memo(StudioEditFooter);
